import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [sessionId, clear]);

  return (
    <main data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-6">
      <div className="glass-panel rounded-lg p-12 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-[#ff1053] mb-6" />
            <h1 className="font-display font-medium text-2xl tracking-tight mb-2">Confirming payment…</h1>
            <p className="text-sm text-white/50 font-light">Hold tight while we verify your order.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#ff1053] mb-6" />
            <h1 data-testid="payment-confirmed-heading" className="font-display font-medium text-2xl tracking-tight mb-2">
              It's official. Always & Forever.
            </h1>
            <p className="text-sm text-white/50 font-light mb-8">
              Payment confirmed. Your pieces go into production now — sublimated or airbrushed to order, shipped from Chicago.
            </p>
            <Link
              data-testid="back-to-shop-link"
              to="/"
              className="inline-block bg-white text-black rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.22em] font-semibold transition-transform duration-300 hover:scale-105"
            >
              Back to the Drop
            </Link>
          </>
        )}
        {(status === "pending" || status === "error") && (
          <>
            <h1 className="font-display font-medium text-2xl tracking-tight mb-2">Still processing</h1>
            <p className="text-sm text-white/50 font-light mb-8">
              We couldn't confirm the payment yet. If you were charged, your order is safe — DM us on TikTok and we'll sort it.
            </p>
            <Link data-testid="back-to-shop-link" to="/" className="inline-block border border-white/30 rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.22em] transition-colors duration-300 hover:bg-white hover:text-black">
              Back to the Drop
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
